import React, {useEffect, useState, createContext, useContext} from 'react';
import {i18n} from 'next-i18next';
import {
	ColorModeScript,
	extendTheme as tempoExtendTheme,
	createLocalStorageManager,
	TempoProvider,
	useColorMode,
	type AccentExtension,
} from '@tempo/core';
import {useRouter} from 'next/router';
import {ANDROID_PLAYER_TOTAL_HEIGHT} from '@constants/ui';
import Bowser from 'bowser';

const STORAGE_KEY = 'webviews-color-mode';

const localStorageManager = createLocalStorageManager(STORAGE_KEY);

const getDirection = (locale: string): 'rtl' | 'ltr' => {
	return i18n?.dir(locale) || 'ltr';
};

const extendTheme = (
	config: Record<string, unknown>,
	extension?: AccentExtension,
) => {
	return tempoExtendTheme({
		...config,
		...extension,
	});
};

type ThemeContextType = {
	setExtension: ((extension?: AccentExtension) => void) | null;
	extension?: AccentExtension;
	isDarkTheme: boolean;
	setIsDarkTheme: ((isDarkTheme: boolean) => void) | null;
};

export const ThemeContext = createContext<ThemeContextType>({
	setExtension: null,
	extension: undefined,
	isDarkTheme: false,
	setIsDarkTheme: null,
});

const ThemeProvider: React.FC<{
	children?: React.ReactNode;
}> = ({children}) => {
	const {locale = 'en', isReady, query} = useRouter();
	const direction = getDirection(locale);
	const [extension, setExtension] = useState<AccentExtension | undefined>();
	const [isDarkTheme, setIsDarkTheme] = useState(false);

	useEffect(() => {
		document.querySelector('body')?.setAttribute('dir', direction);
	}, [direction]);

	if (!isReady) return null;

	const browser = Bowser.getParser(navigator.userAgent);
	const theme = isDarkTheme || query.dark_mode === 'true' ? 'dark' : 'light';

	localStorageManager.set(theme);

	const themeConfig = extendTheme(
		{
			config: {
				direction,
				initialColorMode: theme,
			},
			styles: {
				global: {
					':root': {
						...(browser.getOS().name === 'Android'
							? {
									'--android-safe-area-inset-bottom':
										ANDROID_PLAYER_TOTAL_HEIGHT,
								}
							: {}),
						'--sait':
							'var(--android-safe-area-inset-top, env(safe-area-inset-top))',
						'--saib':
							'var(--android-safe-area-inset-bottom, env(safe-area-inset-bottom))',
						'--sail':
							'var(--android-safe-area-inset-left, env(safe-area-inset-left))',
						'--sair':
							'var(--android-safe-area-inset-right, env(safe-area-inset-right))',
					},
					html: {
						height: '100%',
					},
					body: {
						WebkitTouchCallout: 'none !important',
						userSelect: 'none',
						minHeight: '100vh',
						display: 'flex',
						flexDirection: 'column',
						paddingBottom: 'var(--saib)',
						paddingTop: 'var(--sait)',
					},
					'#__next': {
						flex: '1',
						minHeight: 0,
						display: 'flex',
						flexDirection: 'column',
					},
					a: {
						WebkitUserDrag: 'none',
					},
					// TODO: Need a more global solution for safe area in android to manage it better
					'#chakra-toast-manager-top': {
						top: 'var(--sait) !important',
					},
				},
			},
			space: {
				safeTop: 'var(--sait)',
				safeBottom: 'var(--saib)',
				safeLeft: 'var(--sail)',
				safeRight: 'var(--sair)',
			},
			components: {
				// TODO: Need to be fixed in Tempo web instead
				Modal: {
					variants: {
						foundation: () => ({
							dialog: {
								paddingTop: 'spacing.l', // Modifie uniquement le paddingTop ici
							},
							footer: {
								'& > div': {
									gap: 'spacing.s',
								},
							},
						}),
					},
				},
				Checkbox: {
					sizes: {
						md: {
							label: {
								fontSize: 'body.s',
								fontWeight: 'body.s',
								lineHeight: 'body.s',
								fontFamily: 'body.s',
							},
						},
					},
				},
			},
		},
		extension,
	);

	return (
		<ThemeContext.Provider
			value={{setExtension, extension, isDarkTheme, setIsDarkTheme}}
		>
			<ColorModeScript
				storageKey={STORAGE_KEY}
				initialColorMode={themeConfig.config.initialColorMode}
			/>
			<TempoProvider colorModeManager={localStorageManager} theme={themeConfig}>
				<ColorModeSync />
				{children}
			</TempoProvider>
		</ThemeContext.Provider>
	);
};

function ColorModeSync() {
	const {query} = useRouter();
	const {setColorMode, colorMode} = useColorMode();
	const {isDarkTheme} = useContext(ThemeContext);
	const requestedMode =
		!isDarkTheme && query.dark_mode !== 'true' ? 'light' : 'dark';

	useEffect(() => {
		if (requestedMode !== colorMode) {
			setColorMode(requestedMode);
		}
	}, [requestedMode, colorMode, setColorMode]);
	return null;
}

export default ThemeProvider;
